import React, { ChangeEvent, useEffect, useState } from "react";
import { List, AutoSizer, ListRowRenderer } from 'react-virtualized';

import moment from "moment";

import { FormElementWrapperProps, TextInputProps, SelectDropdownProps, ISelectDropdownOption, SearchSelectProps, TextAreaProps, CheckboxProps, CalenderInputProps } from "../../interfaces/form_elements";

import Calender from "./Calendar";
import OutsideAlerter from "../wrappers/OutsideAlerter";
import { SVGIcon } from "../wrappers/SVGIcon";

import { Icons } from "../../data/Assets";

import { getCanadianDollarsString, getDateString, getElipsisString, getPhoneNumberString } from "../../helpers/utility";
import { onChangeSelect } from "../../helpers/form_elements";

const FormElementWrapper = (props: FormElementWrapperProps) => {
    const { className, length_flex = 1, children, label, label_info, required, errorMsg } = props;

    return (
        <div className={`immersion_admin__form_element_wrapper immersion_admin__form_element_wrapper__flex_${length_flex} ${className}`}>
            {!!label &&
                <label>
                    <span>{label}</span>
                    {!!label_info && <span className="label_info">&nbsp;{label_info}</span>}
                    {!!required && <span className="color_error">&nbsp;*</span>}
                </label>
            }
            {children}
            {errorMsg && errorMsg?.length > 0 && <div className="error_text">{errorMsg}</div>}
        </div>
    );
};

export const TextInput = (props: TextInputProps) => {
    const { name, type, pattern_mask, max_length, read_only, label, label_info, required, errorMsg, placeholder, onChange, value } = props;

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isPreSecVisible, setIsPreSecVisible] = useState(false);
    const [isPostSecVisible, setIsPostSecVisible] = useState(false);

    useEffect(() => {
        if (type === "password") {
            setIsPostSecVisible(true);
        };
    }, [type]);

    useEffect(() => {
        if (pattern_mask === "currency_cad") {
            setIsPreSecVisible(true);
        };
    }, [pattern_mask]);

    const onClickInputButton = () => {
        if (type === 'password') {
            setIsPasswordVisible(!isPasswordVisible);
        };
    };

    const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;

        let value = e.target.value;

        if (pattern_mask === 'integer') {
            value = value.replace(/\D/g, '');
        } else if (pattern_mask === 'float') {
            value = value.replace(/[^0-9.]/g, '');
            value = value.replace(/\.(?=.*\.)/, '');
        } else if (pattern_mask === "currency_cad") {
            value = getCanadianDollarsString(value);
        } else if (pattern_mask === "phone_number") {
            value = getPhoneNumberString(value);
        } else if (pattern_mask === "doc_number") {
            value = value.replace(/[^a-zA-Z0-9]/g, '');
            value = value.toUpperCase();
        }

        if (max_length) {
            value = value.slice(0, max_length);
        }

        onChange(value);
    };

    return (
        <FormElementWrapper
            className={`immersion_admin__text_input_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
        >
            <div className={`input_group ${read_only ? 'input_group__readonly' : ''}`}>
                {isPreSecVisible &&
                    <div className="input_group__pre_sec">
                        {pattern_mask === "currency_cad" && <div className="color_dark">$</div>}
                    </div>
                }
                <input
                    name={name}
                    type={type === "password" && isPasswordVisible ? "text" : type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChangeInput}
                    readOnly={!!read_only}
                />
                {isPostSecVisible &&
                    <div className="input_group__post_sec">
                        {type === "password" &&
                            <button onClick={onClickInputButton} type="button">
                                <SVGIcon src={!isPasswordVisible ? Icons.EyeOffFill : Icons.EyeFill} />
                            </button>
                        }
                    </div>
                }
            </div>
        </FormElementWrapper>
    );
};

export const TextAreaInput = (props: TextAreaProps) => {
    const { name, max_length, read_only, label, label_info, required, errorMsg, placeholder, onChange, value } = props;

    const onChangeInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (read_only) return;

        let value = e.target.value;

        if (max_length) {
            value = value.slice(0, max_length);
        };

        onChange(value);
    };

    return (
        <FormElementWrapper
            className={`immersion_admin__text_input_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
        >
            <div className={`input_group ${read_only ? 'input_group__readonly' : ''}`}>
                <textarea
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChangeInput}
                    readOnly={!!read_only}
                    rows={4}
                />
            </div>
        </FormElementWrapper>
    );
};

export const SelectDropdown = (props: SelectDropdownProps) => {
    const { read_only, length_flex = 1, label, label_info, required, errorMsg, placeholder, onChange, value, options } = props;

    const [open, setOpen] = useState(false);

    const onToggleDropdown = () => {
        if (!options.length || read_only) return;
        setOpen(!open);
    };

    return (
        <FormElementWrapper
            className={`immersion_admin__select_dropdown_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
            length_flex={length_flex}
        >
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className={`immersion_admin__select_dropdown__container ${open ? 'immersion_admin__select_dropdown__container__opened' : ''}`}>
                    <button
                        className={`immersion_admin__select_dropdown__btn ${read_only ? 'immersion_admin__select_dropdown__btn__readonly' : ''}`}
                        onClick={onToggleDropdown}
                    >
                        {
                            value.label?.length
                                ? <span className="color_dark">{getElipsisString(value.label, 100)}</span>
                                : <span className="color_grey">{placeholder?.length ? placeholder : '-- Select --'}</span>
                        }
                        <div className="icons_sec">
                            <SVGIcon src={Icons.TriangleUpDownFill} color={read_only ? 'grey' : 'primary'} />
                        </div>
                    </button>
                    {open &&
                        <div className="immersion_admin__ssd_dropdown__content">
                            {(!!placeholder?.length && !required) &&
                                <button onClick={() => onChangeSelect({ read_only, setOpen, onChange, option: { value: "", label: "" } })}>{placeholder}</button>
                            }
                            {options.map((option, index) =>
                                <button
                                    key={index + option.value}
                                    className={option.readOnly ? 'immersion_admin__ssd_dropdown__option__readonly' : ''}
                                    onClick={() => option.readOnly
                                        ? null
                                        : onChangeSelect({ read_only, setOpen, onChange, option })
                                    }
                                >
                                    {option.label}
                                </button>
                            )}
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </FormElementWrapper>
    );
};

export const SearchSelect = (props: SearchSelectProps) => {
    const { read_only, length_flex = 1, label, label_info, required, errorMsg, placeholder, onChange, value, options } = props;

    const [open, setOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<ISelectDropdownOption[]>(options);
    const [search, setSearch] = useState("");

    useEffect(() => {
        setSearch(value.label);

        // eslint-disable-next-line
    }, [value]);

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;
        setOpen(true);

        const val = e.target.value;
        setSearch(val);

        const filteredOptions = options.filter(option => option.label.toLowerCase().includes(val.toLowerCase()));
        setFilteredOptions(filteredOptions);
    };

    const onBlurInput = () => {
        if (read_only) return;

        const option = options.find(option => option.label === search);
        if (option) {
            onChange(option);
        } else {
            onChange({ value: "", label: "" });
        };
    };

    const rowRenderer: ListRowRenderer = ({ index, style }) => {
        return (
            <div
                key={index}
                style={{
                    ...style,
                    whiteSpace: "pre",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%"
                }}
            >
                <button onClick={() => onChangeSelect({ read_only, setOpen, onChange, option: filteredOptions[index] })}>
                    {filteredOptions[index].label}
                </button>
            </div>
        );
    };

    return (
        <FormElementWrapper
            className={`immersion_admin__search_select_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
            length_flex={length_flex}
        >
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className={`immersion_admin__search_select__container ${open ? 'immersion_admin__search_select__container__opened' : ''}`}>
                    <div className={`immersion_admin__search_select__input_group ${read_only ? 'immersion_admin__search_select__input_group__readonly' : ''}`}>
                        <input
                            type="text"
                            className={`immersion_admin__search_select__input`}
                            onChange={onChangeSearch}
                            onBlur={onBlurInput}
                            placeholder={placeholder}
                            value={search}
                        />
                        <SVGIcon src={Icons.Search} color={read_only ? 'grey' : 'primary'} />
                    </div>
                    {open &&
                        <div className="immersion_admin__ssd_dropdown__virtualized_content">
                            {
                                filteredOptions.length === 0
                                    ? <div><div><button>No records found!</button></div></div>
                                    : <AutoSizer disableWidth>
                                        {() => {
                                            return (
                                                <List
                                                    width={1}
                                                    height={filteredOptions.length > 10 ? 400 : filteredOptions.length * 40}
                                                    overscanRowsCount={2}
                                                    rowCount={filteredOptions.length}
                                                    rowHeight={40}
                                                    rowRenderer={rowRenderer}
                                                    containerStyle={{ width: "100%", maxWidth: "100%" }}
                                                    style={{ width: "100%" }}
                                                />
                                            );
                                        }}
                                    </AutoSizer>
                            }
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </FormElementWrapper>
    );
};

export const Checkbox = (props: CheckboxProps) => {
    const { label, label_info, required, errorMsg, checked, onChange } = props;

    const onChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked);
    };

    return (
        <FormElementWrapper
            className={`immersion_admin__checkbox_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
        >
            <div className="input_group">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onChangeCheckbox}
                    id={label}
                />
                {label && (
                    <label htmlFor={label}>{label}</label>
                )}
            </div>
        </FormElementWrapper>
    );
};

export const CalenderInput = (props: CalenderInputProps) => {
    const { read_only, label, label_info, required, errorMsg, placeholder, onChange, value, format, tooltip } = props;

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const onOpenCalender = () => {
        if (read_only) return;
        setOpen(true);
    };

    const onChangeCalender = (date: Date, keepOpen?: boolean) => {
        if (read_only) return;

        if (!keepOpen) setOpen(false);

        setInputValue(getDateString(date));
        onChange(getDateString(date));
    };

    const onClearCalender = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        if (read_only) return;
        setInputValue("");
        onChange("");

        e.stopPropagation();
    };

    const onBlurInput = () => {
        if (read_only) return;

        if (inputValue.length < 10) {
            if (value?.trim()?.length) {
                setInputValue(getDateString(value));
            } else {
                setInputValue("");
            };
        };
    };

    const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;

        setOpen(true);

        let value = e.target.value;

        if (inputValue.length > value.length) {
            setInputValue(value);
            return;
        };

        value = value.replace(/[^0-9]/g, '');

        if (format === 'mm/dd/yyyy') {
            if (value.length >= 8) {
                value = value.slice(0, 8);
                value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;

                const date = moment.utc(value, 'MM/DD/YYYY', true);
                if (!date.isValid()) {
                    value = moment.utc().format('MM/DD/YYYY');
                    onChangeCalender(moment.utc().toDate(), true);
                } else {
                    if (parseInt(date.format('YYYY')) < 1960) {
                        value = '01/01/1960';
                        onChangeCalender(moment.utc(value, 'MM/DD/YYYY').toDate(), true);
                    } else if (parseInt(date.format('YYYY')) > 2100) {
                        value = '12/31/2100';
                        onChangeCalender(moment.utc(value, 'MM/DD/YYYY').toDate(), true);
                    } else {
                        onChangeCalender(date.toDate(), true);
                    };
                };
            } else {
                if (value.length === 1) {
                    if (parseInt(value) > 1) {
                        value = '0' + value;
                        value += '/';
                    };
                } else if (value.length === 2) {
                    if (parseInt(value) === 0) {
                        value = '01'
                    } else if (parseInt(value) > 12) {
                        value = '12'
                    };
                    value += '/';
                } else if (value.length === 3) {
                    const lastDigit = parseInt(value[2]);
                    const month = value.slice(0, 2);
                    const daysInMonth = moment.utc(`${month}/01/2024`, 'MM/DD/YYYY', true).daysInMonth();

                    if (lastDigit > parseInt(daysInMonth.toString()[0])) {
                        value = value.slice(0, 2) + '/0' + lastDigit + '/';
                    } else {
                        value = value.slice(0, 2) + '/' + lastDigit;
                    };
                } else if (value.length === 4) {
                    const month = value.slice(0, 2);
                    const daysInMonth = moment.utc(`${month}/01/2024`, 'MM/DD/YYYY', true).daysInMonth();
                    const date = value.slice(2, 4);

                    if (parseInt(date) > daysInMonth) {
                        value = `${month}/${daysInMonth}/`;
                    } else {
                        value = `${month}/${date}/`;
                    };
                } else if (value.length >= 5 && value.length <= 7) {
                    const month = value.slice(0, 2);
                    const date = value.slice(2, 4);
                    const yearStr = value.slice(4);

                    value = `${month}/${date}/${yearStr}`;
                };
            };
        };

        setInputValue(value);
    };

    const onElementFocus = () => {
        if (read_only) return;
        setOpen(true);
    };

    const onElementBlur = () => {
        if (read_only) return;
        setOpen(false);
    }

    return (
        <FormElementWrapper
            className={`immersion_admin__calender_input_wrapper`}
            label={label ?? ''}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
            tooltip={tooltip}
            onElementFocus={onElementFocus}
            onElementBlur={onElementBlur}
        >
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div
                    className={`immersion_admin__calender_input__container ${open ? 'immersion_admin__calender_input__container__opened' : ''}`}
                    onFocus={onElementFocus}>
                    <div
                        className={`immersion_admin__calender_input__cont ${read_only ? 'immersion_admin__calender_input__cont__readonly' : ''}`}
                        onClick={onOpenCalender}
                    >
                        <input
                            type="text"
                            onChange={onChangeInput}
                            onBlur={onBlurInput}
                            placeholder={placeholder}
                            value={inputValue}
                            readOnly={read_only}
                        />
                        <span className="immersion_admin__calender_input__cont__span">
                            {value?.trim()?.length > 0 && !read_only &&
                                <span onClick={onClearCalender}>
                                    <SVGIcon src={Icons.X} color='primary' />
                                </span>
                            }
                            <SVGIcon src={Icons.Calendar3Fill} color={read_only ? 'grey' : 'primary'} />
                        </span>
                    </div>
                    {open &&
                        <div className="immersion_admin__calender_input__content">
                            <Calender
                                selected={value?.trim()?.length ? moment.utc(value, 'MM/DD/YYYY', true).toDate() : moment.utc().toDate()}
                                setSelected={onChangeCalender}
                            />
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </FormElementWrapper>
    );
};